import {
  ActionCreator,
  createSlice,
  PayloadAction,
  ThunkAction,
  ThunkDispatch,
} from '@reduxjs/toolkit';
import { captureException } from '@sentry/nextjs';
import type { Response } from 'express';
import { getVerticalOfCategoryID } from '@sbt-web/category-utils';
import {
  getConfigPage,
  searchGallerized,
  searchGeoboosted,
  searchItems,
} from '@sbt-web/network/client';
import {
  CategoryID,
  HTTPStatusCode,
  NetError,
  SearchParam,
  SearchParams,
} from '@sbt-web/network/types';
import { isEmpty, TuttaItalia } from '@sbt-web/utils';
import { isSearchEligibleForGeoboost } from '@/client/components/Adv/Geoboost/shared';
import { FiltersConfigState } from '@/reducers/filtersConfig';
import { updateFiltersLabelsBulk, type SearchState } from '@/reducers/search';
import { ADS_PER_PAGE } from '@/shared/constants';
import { DeviceType } from '@/shared/helpers/DeviceDetector';
import { getRankedItems, RankedItem } from '@/shared/helpers/Rank/store';
import { DecoratedItem, GalleryItem } from '@/shared/models';
import {
  afterPlugins,
  beforePlugins,
  ListingPlugin,
  matchCategories,
  shouldAddToListing,
} from '@/shared/models/ListingPlugins';
import { all } from '@/shared/models/ListingPlugins/matchers';
import { galleryPosition } from '@/shared/models/ListingPlugins/position';
import {
  type ErrorSeverity,
  getErrorMessage,
  registerError,
} from '@/tools/errorHelpers';
import { providePlugins } from '@/tools/listingPlugins';
import { FiltersStore, SPECIAL_FILTERS } from '@/tools/search/getFiltersStore';
import type { RootState } from '..';

export type ItemsState = {
  list: DecoratedItem[];
  rankedList: RankedItem[];
  total: number;
  totalPages: number;
  hasError: boolean;
  loading: boolean;
};

const initialState: ItemsState = {
  list: [],
  rankedList: [],
  total: 0,
  totalPages: 0,
  hasError: false,
  loading: false,
};

interface SuccessPayload {
  list: DecoratedItem[];
  rankedList: RankedItem[];
  total: number;
}

const itemsSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {
    updateItemsRequest: (state) => {
      state.loading = true;
      state.hasError = false;
    },

    updateItemsSuccess: (state, action: PayloadAction<SuccessPayload>) => {
      state.list = action.payload.list;
      state.rankedList = action.payload.rankedList;
      state.total = action.payload.total;
      state.totalPages = Math.ceil(action.payload.total / ADS_PER_PAGE);
      state.loading = false;
      state.hasError = false;
    },

    updateItemsFailure: (state) => {
      state.loading = false;
      state.hasError = true;
    },
  },
});

export const items = itemsSlice.reducer;

export const { updateItemsRequest, updateItemsSuccess, updateItemsFailure } =
  itemsSlice.actions;

type ExternalActions = ReturnType<typeof updateFiltersLabelsBulk>;

type UpdateItemsActions =
  | ReturnType<typeof updateItemsFailure>
  | ReturnType<typeof updateItemsRequest>
  | ReturnType<typeof updateItemsSuccess>;

export type ThunkActions = UpdateItemsActions | ExternalActions;

export const updateItems: ActionCreator<
  ThunkAction<Promise<ThunkActions>, RootState, void, ThunkActions>
> = (
  res?: Response
): ((
  d: ThunkDispatch<RootState, void, ThunkActions>,
  g: () => RootState
) => Promise<ThunkActions>) => {
  return async (dispatch, getState): Promise<ThunkActions> => {
    dispatch(updateItemsRequest());
    try {
      const state = getState();
      const params = mapSearchStateToSearchParams(
        state.search,
        state.filtersConfig
      );
      const environmentId = state.env.id || undefined;

      const itemsPromise = searchItems(
        process.env.ANUBI_BASE_URL || process.env.NEXT_PUBLIC_HADES_BASE_URL,
        params,
        environmentId,
        true
      );

      const galleryPromise = searchGallerized(
        process.env.ANUBI_BASE_URL || process.env.NEXT_PUBLIC_HADES_BASE_URL,
        { ...params, [SearchParam.PageSize]: 3 },
        environmentId,
        true
      );

      const geoboostPromise = isSearchEligibleForGeoboost(state.search)
        ? searchGeoboosted(
            process.env.ANUBI_BASE_URL ||
              process.env.NEXT_PUBLIC_HADES_BASE_URL,
            { ...params, [SearchParam.PageSize]: 5 },
            environmentId,
            true
          ).then((res) => res.body.ads)
        : Promise.resolve([]);

      let searchResponse;
      try {
        searchResponse = await itemsPromise;
      } catch (e) {
        if (res) {
          handleInvalidResponse(res, 'FATAL', e);
        }
        return dispatch(updateItemsFailure());
      }
      const items = searchResponse.body;

      if (process.env.NEXT_RUNTIME === 'nodejs' && items.filterLabels) {
        dispatch(updateFiltersLabelsBulk(items.filterLabels));
      }

      const galleryResponse = await galleryPromise;
      const galleryList = galleryResponse.body.ads;
      const pageGalleryCount = galleryList.length;
      const pageAdsCount = items.ads.length;
      const deviceType = state.system.deviceType;

      const galleryPlugins = galleryList.map(
        (item): ListingPlugin => ({
          model: new GalleryItem(item),
          position: galleryPosition(),
          categories: all(),
        })
      );

      const boostedItems = await geoboostPromise;

      const advFrequency = await getAdvFrequency(params, deviceType);
      const validPlugins = providePlugins(advFrequency, pageAdsCount)
        .flatMap((p: ListingPlugin) => {
          if (p.model.kind === 'GalleryPlaceholder') {
            return galleryPlugins;
          } else if (p.model.kind === 'GeoboostItem') {
            p.model.boostedItems = boostedItems;
            return [p];
          } else {
            return [p];
          }
        })
        .filter(shouldAddToListing(pageAdsCount, pageGalleryCount, items.total))
        .filter(matchCategories(state.search.category.id));

      const rankedList: Array<RankedItem> = [];
      const decoratedItems = items.ads.map((item, itemIndex) => {
        const before = validPlugins.filter(
          beforePlugins(pageAdsCount, itemIndex)
        );
        const after = validPlugins.filter(
          afterPlugins(pageAdsCount, itemIndex)
        );

        rankedList.push(...getRankedItems([...before, item, ...after]));
        return new DecoratedItem(before, item, after);
      });

      return dispatch(
        updateItemsSuccess({
          list: decoratedItems,
          rankedList,
          total: items.total,
        })
      );
    } catch (error) {
      captureException(`Failed to call Anubi: ${getErrorMessage(error)}`, {
        level: 'fatal',
      });

      return dispatch(updateItemsFailure());
    }
  };
};

function handleInvalidResponse(
  res: Response,
  severity: ErrorSeverity,
  e: unknown
): void {
  let calculatedStatus: HTTPStatusCode = 500;
  let info;
  let errors;
  if (e instanceof NetError) {
    calculatedStatus = e.cause.status;
    info = e.message;
    errors = e.cause.errors;
  } else if (typeof e === 'string' && e.includes('aborted')) {
    calculatedStatus = HTTPStatusCode.GatewayTimeout;
  }
  registerError(res, calculatedStatus, severity, {
    errors,
    info,
    service: 'ANUBI',
    message: new Error('Request failed to Anubi.'),
  });
}

export function mapSearchStateToSearchParams(
  searchState: SearchState,
  filtersConfigState: FiltersConfigState
): SearchParams {
  let params: SearchParams = {
    q: searchState.query,
    c:
      searchState.category.id !== CategoryID.Tutte
        ? searchState.category.id
        : undefined,
    r:
      searchState.geo.region.friendlyName !== TuttaItalia.friendlyName
        ? searchState.searchNearRegions
          ? searchState.geo.region.neighbors
          : searchState.geo.region.id
        : undefined,
    ci: searchState.geo?.city?.id,
    to: searchState.geo?.town?.id,
    z: searchState.geo?.zone?.id,
    t: searchState.adType,
    qso: searchState.qso,
    shp: searchState.includeShippableOnly,
    urg: searchState.includeUrgent,
    sort: searchState.category.orders[searchState.orderIndex],
    lim: ADS_PER_PAGE,
    start: getStartingIndex(searchState),
  };

  if (searchState.radiusSearch != undefined) {
    const obj = {
      lat: searchState.radiusSearch.center.lat.toString(),
      lon: searchState.radiusSearch.center.lng.toString(),
      rad: searchState.radiusSearch.radiusMeters.toString(),
    };
    params = { ...params, ...obj };
  }
  const currentConfig = FiltersStore.fromSerialized(
    filtersConfigState.filtersStore
  );

  if (!isEmpty(searchState.filters) && currentConfig) {
    currentConfig.sparseFilters.forEach((filter) => {
      // * special filters are treated already singularly and should not be included in the query anymore
      if (SPECIAL_FILTERS.has(filter.uri)) return;
      const query = filter.queryString as SearchParam;
      if (filter && searchState.filters[query]) {
        params[query] = searchState.filters[query];
      }
    });
  }

  // We want to remove items with no price from the search results if it is sorted by price in ascending order
  if (params.sort === 'priceasc') {
    params.ps = searchState?.filters?.ps ?? '1';
  }

  return params;
}

export function getStartingIndex(searchState: SearchState): number | undefined {
  return searchState.page > 0 ? (searchState.page - 1) * ADS_PER_PAGE : 0;
}

export const getAdvFrequency = async (
  params: SearchParams,
  deviceType?: DeviceType | null
): Promise<number> => {
  let macro = 'subito';
  if (params.c) {
    macro = getVerticalOfCategoryID(params.c as CategoryID);
  }
  let deviceTypeLocal = 'desktop';

  if (deviceType === 'mobile') {
    deviceTypeLocal = 'msite';
  }

  try {
    const { body } = await getConfigPage(
      process.env.NEXT_PUBLIC_HADES_BASE_URL,
      { property: deviceTypeLocal, name: 'listing', vertical: macro }
    );

    if (body) {
      return body.page_level_settings?.frequency ?? 3;
    } else {
      return 3; // default adv frequency
    }
  } catch (e) {
    console.log('Error getting config page', e);
    return 3;
  }
};
